import { useEffect, useState } from 'react';
import { api, type Vehicle } from '../lib/api.ts';
import { formatVin } from '../lib/obd/vin.ts';
import { formatDay, usePrefs } from '../lib/prefs.tsx';

// The vehicle identity card, shared by the Scan and History screens so the two
// cannot drift apart. History previously showed a scan's results without ever
// saying which vehicle they belonged to.

export function VehicleSpecs({ vehicle }: { vehicle: Vehicle | null }) {
  return (
    <div className="spec-grid">
      <Spec k="Year" v={vehicle?.modelYear ?? '\u2014'} />
      <Spec k="Make" v={vehicle?.make ?? '\u2014'} />
      <Spec k="Model" v={vehicle?.model ?? '\u2014'} />
      <Spec k="Trim" v={vehicle?.trim ?? '\u2014'} />
      <Spec k="Engine" v={vehicle?.engine ?? '\u2014'} />
      <Spec k="Body" v={vehicle?.bodyClass ?? '\u2014'} />
      <Spec
        k="Decoded by"
        v={
          vehicle?.decodedBy === 'vpic'
            ? 'NHTSA vPIC'
            : vehicle?.decodedBy === 'manual'
              ? 'Entered by hand'
              : vehicle?.decodedBy === 'local'
                ? 'VIN table only'
                : '\u2014'
        }
      />
    </div>
  );
}

/** Identity header: name, VIN, and the spec grid. */
export function VehicleHeader({ vehicle, vin }: { vehicle: Vehicle | null; vin: string | null }) {
  return (
    <>
      <span className="eyebrow">Vehicle</span>
      <p className="title">
        {vehicle?.label ||
          [vehicle?.modelYear, vehicle?.make, vehicle?.model].filter(Boolean).join(' ') ||
          'Unidentified vehicle'}
      </p>
      <p className="vin">{vin ? formatVin(vin) : 'VIN not reported'}</p>
      <VehicleSpecs vehicle={vehicle} />
    </>
  );
}

function Spec({ k, v }: { k: string; v: string | number }) {
  return (
    <div className="spec">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

/**
 * The parts of a vehicle record we captured and never showed: the whole NHTSA
 * payload rather than six fields of it, a free-text note, and the channel list
 * the vehicle advertised.
 */
export function VehicleExtras({ vehicle, supportedPids }: { vehicle: Vehicle | null; supportedPids: number[] }) {
  const { dateFormat } = usePrefs();
  const [openSpecs, setOpenSpecs] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [redecoding, setRedecoding] = useState(false);
  const [redecodeMessage, setRedecodeMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [draft, setDraft] = useState({ model: '', trim: '', engine: '', bodyClass: '' });

  useEffect(() => {
    setNotes(vehicle?.notes ?? '');
    setDirty(false);
    setDraft({
      model: vehicle?.model ?? '',
      trim: vehicle?.trim ?? '',
      engine: vehicle?.engine ?? '',
      bodyClass: vehicle?.bodyClass ?? '',
    });
  }, [vehicle?.vin, vehicle?.notes, vehicle?.model, vehicle?.engine, vehicle?.bodyClass, vehicle?.trim]);

  if (!vehicle) return null;

  const missing = (['model', 'engine', 'bodyClass'] as const)
    .filter((f) => !vehicle[f])
    .map((f) => (f === 'bodyClass' ? 'body' : f));

  async function redecode() {
    setRedecoding(true);
    setRedecodeMessage(null);
    try {
      const updated = await api.redecodeVin(vehicle!.vin);
      setRedecodeMessage({
        tone: 'ok',
        text: `Decoded: ${[updated.modelYear, updated.make, updated.model, updated.engine].filter(Boolean).join(' ')}`,
      });
    } catch (err) {
      setRedecodeMessage({ tone: 'error', text: (err as Error).message });
    } finally {
      setRedecoding(false);
    }
  }

  async function saveDetails() {
    setSavingDetails(true);
    try {
      await api.updateVehicle(vehicle!.vin, draft);
      setEditing(false);
    } finally {
      setSavingDetails(false);
    }
  }

  // vPIC returns well over a hundred keys, most of them empty. Show the ones
  // that carry a value, sorted, rather than a wall of blanks.
  const specs = Object.entries(vehicle.decoded ?? {})
    .filter(([k, v]) => v && v !== 'Not Applicable' && !/^Error|^Suggested|^Additional/.test(k))
    .sort(([a], [b]) => a.localeCompare(b));

  async function saveNotes() {
    setSavingNotes(true);
    try {
      await api.labelVehicle(vehicle!.vin, vehicle!.label ?? '', notes);
      setDirty(false);
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 10, marginTop: 14 }}>
      {missing.length > 0 && (
        <div className="banner warn small">
          <strong>Missing {missing.join(', ')}.</strong> The online decode did not run, which usually means the scan
          happened while joined to the adapter's Wi-Fi with no internet route. Retry it now, or type the values in
          yourself.
        </div>
      )}

      <div className="row wrap" style={{ gap: 8 }}>
        <button className="tiny" onClick={redecode} disabled={redecoding}>
          {redecoding ? 'Decoding…' : 'Re-decode VIN'}
        </button>
        <button className="tiny ghost" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Cancel' : 'Edit details'}
        </button>
        {specs.length > 0 && (
          <button className="tiny ghost" onClick={() => setOpenSpecs((v) => !v)}>
            {openSpecs ? 'Hide' : 'Show'} full spec sheet ({specs.length} fields)
          </button>
        )}
        {supportedPids.length > 0 && (
          <span className="pill" title={supportedPids.map((p) => '0x' + p.toString(16).toUpperCase()).join(' ')}>
            {supportedPids.length} data channels advertised
          </span>
        )}
        <span className="pill dim">first seen {formatDay(vehicle.firstSeenAt, dateFormat)}</span>
      </div>

      {redecodeMessage && <div className={`banner ${redecodeMessage.tone} small`}>{redecodeMessage.text}</div>}

      {editing && (
        <div className="grid-2" style={{ margin: 0 }}>
          {(['model', 'trim', 'engine', 'bodyClass'] as const).map((field) => (
            <label className="field" key={field}>
              <span>{field === 'bodyClass' ? 'Body' : field}</span>
              <input
                type="text"
                value={draft[field]}
                onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
                placeholder={field === 'engine' ? 'e.g. 6.7L 8-cyl Diesel' : ''}
              />
            </label>
          ))}
          <div className="row">
            <button className="primary" onClick={saveDetails} disabled={savingDetails}>
              Save details
            </button>
          </div>
        </div>
      )}

      {openSpecs && (
        <div className="scroll-x">
          <table className="offers">
            <tbody>
              {specs.map(([k, v]) => (
                <tr key={k}>
                  <td className="dim" style={{ width: '45%' }}>
                    {k.replace(/([a-z])([A-Z])/g, '$1 $2')}
                  </td>
                  <td>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <label className="field">
        <span>Notes</span>
        <textarea
          value={notes}
          rows={2}
          placeholder="What you found, what you replaced, what to check next time."
          onChange={(e) => {
            setNotes(e.target.value);
            setDirty(true);
          }}
        />
      </label>
      {dirty && (
        <div className="row">
          <button onClick={saveNotes} disabled={savingNotes}>
            Save notes
          </button>
        </div>
      )}
    </div>
  );
}
